// apps/web/src/lib/briefing-attribution.ts
//
// KAN-482 F8 — the receiving half of `briefing:email_clicked`.
//
// Deliberately its own module rather than an addition to `lib/analytics.ts`:
// that file is the KAN-32 analytics core (identity, pageview, replay) and is
// under concurrent edit by the R13 reverse-proxy lane. Keeping F8 separate makes
// the two changes independent.

import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { capture } from '@intelhub/analytics/client'
import { parseBriefingClick, stripBriefingParams } from '@intelhub/shared/briefing-link'
import { useAuth } from '@/contexts/AuthContext'

/**
 * Emits `briefing:email_clicked` when the page was reached from a UTM-tagged
 * briefing CTA, then scrubs the tag out of the URL.
 *
 * WHY IT WAITS FOR THE SESSION. `useAnalyticsIdentity` binds `distinct_id` to
 * the ZITADEL subject once `/me` resolves. Firing before that would attribute
 * the click to an anonymous id that is discarded on reload (P1 —
 * `persistence: 'memory'`), so the one event that is supposed to prove "the
 * briefing brought this CISO back" would be the one event not joined to them.
 * Mounted after `useAnalyticsIdentity()` in `<AnalyticsBridge/>`, so identify
 * has already run in the same commit.
 *
 * WHY IT SCRUBS THE URL. R22's `sanitizeUrl` reduces `$current_url` and
 * `$pathname` to route patterns, so the tag cannot reach PostHog through a
 * captured property. It CAN still reach a third party through the browser's own
 * `Referer` header on the next outbound link — which R22 has no reach over. The
 * `replace: true` navigation closes that, and also stops a bookmark or a shared
 * URL from carrying a stale campaign tag forever.
 *
 * WHY IT IS ONCE PER PAGE LOAD. The ref guard, not the URL scrub, is what makes
 * this idempotent. Relying on the scrub alone would re-fire on any render that
 * observed the pre-scrub location — React 18 StrictMode double-invokes effects
 * in development, which is exactly that render.
 *
 * ACCEPTED UNDER-COUNT. A recipient who is signed out when they click sees
 * `LoginPage` rendered in place, so the tagged URL survives; but the ZITADEL
 * round trip returns to the app root and the tag is lost, so that click is not
 * counted. Under-counting is the correct direction to fail: no alternative
 * short of persisting the tag across the redirect is available, and P1 forbids
 * new client-side storage.
 */
export function useBriefingAttribution(): void {
  const location = useLocation()
  const navigate = useNavigate()
  const { isLoading } = useAuth()
  const handled = useRef(false)

  useEffect(() => {
    if (handled.current) return
    // Unresolved session => wait, don't guess. The next render re-runs this.
    if (isLoading) return

    const attribution = parseBriefingClick(location.search, new Date())
    if (!attribution) return

    handled.current = true
    // Through the wrapper, never `posthog.capture` directly: the R4 envelope is
    // stamped in `before_send`, and bypassing it is precisely what left 62
    // `$pageview` events with no envelope and the weekly-active-CISOs insight
    // reading zero.
    capture('briefing:email_clicked', attribution)

    navigate(
      { pathname: location.pathname, search: stripBriefingParams(location.search) },
      { replace: true },
    )
  }, [isLoading, location.pathname, location.search, navigate])
}
